import { useEffect } from "react";

const SUFFIX = "Break the Test";
const SITE = "https://breakthetest.app";

/**
 * Sets document.title for a route.
 *
 * The static title in index.html describes the home page, so every other route
 * inherited it: the tab, the browser history entry, and the title a search
 * result or shared link shows were all "Break the Test: Free SAT, ACT, AP &
 * College Essay Practice" regardless of the page. Public, indexable pages set
 * their own here. Restores the previous title on unmount so a client-side
 * navigation back to the home page does not leave a stale one behind.
 *
 * @param title Page-specific title, without the brand suffix. Pass null to keep
 *   whatever is already set (used for the home page, which index.html covers).
 */
export function usePageTitle(title: string | null) {
  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = `${title} · ${SUFFIX}`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/**
 * Sets the per-route description and social/answer-engine metadata.
 *
 * index.html carries the home page's description, og: and twitter: tags, and
 * every other route inherited them. Measured 2026-07-30 on the live site: /faq,
 * /methodology, /privacy and all 22 blog posts served the homepage description
 * and og:title "Break the Test". So a shared post previewed as the generic
 * homepage, and any answer engine reading og:description got a summary that had
 * nothing to do with the page it was describing. Titles were already per-route;
 * everything else was not.
 *
 * This matters because scripts/prerender.mjs snapshots the DOM after render, so
 * whatever these hooks set is what ships in the static HTML a crawler reads.
 *
 * Also owns document.title, so the title and og:title cannot drift: passing the
 * same string to two hooks is what let /try ship og:title "Try a real question"
 * against <title> "Try a real SAT question".
 *
 * Restores previous values on unmount so a client-side navigation does not leave
 * stale metadata behind.
 */
export function usePageMeta({
  title,
  description,
  path,
  type = "website",
  image,
}: {
  /** Same page-specific title passed to usePageTitle, without the brand suffix. */
  title: string;
  description: string;
  /** Route path, e.g. "/faq" or "/blog/some-post/". Used for og:url. */
  path: string;
  /** "article" for journal posts, "website" otherwise. */
  type?: "website" | "article";
  /**
   * Absolute-from-root path to the 1200x630 social card, e.g. "/og/slug.png".
   * Defaults to the site card. og:image used to be a 512px favicon with
   * twitter:card "summary", so every share rendered as a small generic square
   * saying nothing about the page.
   */
  image?: string;
}) {
  useEffect(() => {
    const url = `${SITE}${path}`;
    const fullTitle = `${title} · ${SUFFIX}`;
    const previousTitle = document.title;
    document.title = fullTitle;
    const set = (selector: string, attr: "content", value: string) => {
      const el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) return null;
      const previous = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return () => {
        if (previous !== null) el.setAttribute(attr, previous);
      };
    };

    const card = `${SITE}${image ?? "/og/default.png"}`;
    const restores = [
      set('meta[name="description"]', "content", description),
      set('meta[property="og:image"]', "content", card),
      set('meta[property="og:image:alt"]', "content", fullTitle),
      set('meta[name="twitter:image"]', "content", card),
      set('meta[property="og:description"]', "content", description),
      set('meta[property="og:title"]', "content", fullTitle),
      set('meta[property="og:url"]', "content", url),
      set('meta[property="og:type"]', "content", type),
      set('meta[name="twitter:description"]', "content", description),
      set('meta[name="twitter:title"]', "content", fullTitle),
    ].filter(Boolean) as (() => void)[];

    return () => {
      document.title = previousTitle;
      restores.forEach((r) => r());
    };
  }, [title, description, path, type, image]);
}
